import { useEffect, useImperativeHandle, useRef } from "react";
import L from "leaflet";
import "@geoman-io/leaflet-geoman-free";
import { TILE_LAYERS, MULTI_COLOR, NONE_COLOR, PIN_SVG, tourneeColor, zoneColor } from "../../lib/mapStyle";

const OCCITANIE_BOUNDS = L.latLngBounds([42.33, -0.33], [45.05, 4.85]);
const LABEL_MIN_ZOOM = 12;

function escapeHtml(s) {
  return String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}

/**
 * Carte Leaflet pilotée de façon impérative (les couches sont recréées ou
 * restylées par effets React). Méthodes exposées via `ref` :
 *   startDraw(), cancelDraw(), startEditZone(id), finishEditZone() -> geometry,
 *   cancelEditZone(), fitGeo(geojson, maxZoom), flyTo(lat, lon, zoom), fitOccitanie()
 */
export function MapView({
  ref,
  baseLayer,
  departements,
  communes,
  zones,
  ctx,
  hidden,
  show,
  highlight,
  marker,
  onMapClick,
  onDrawn,
  onDrawStateChange,
}) {
  const elRef = useRef(null);
  const mapRef = useRef(null);
  const tileRef = useRef(null);
  const depLayerRef = useRef(null);
  const communesLayerRef = useRef(null);
  const zonesLayerRef = useRef(null);
  const zoneLayersById = useRef({});
  const markerRef = useRef(null);
  const editingRef = useRef(null);
  const cbRef = useRef({});
  cbRef.current = { onMapClick, onDrawn, onDrawStateChange };

  // --- Initialisation (une seule fois) ---
  useEffect(() => {
    const map = L.map(elRef.current, {
      zoomControl: false,
      minZoom: 6,
      maxBounds: OCCITANIE_BOUNDS.pad(0.6),
      worldCopyJump: false,
    });
    map.fitBounds(OCCITANIE_BOUNDS);
    L.control.zoom({ position: "bottomright" }).addTo(map);
    L.control.scale({ position: "bottomright", imperial: false }).addTo(map);

    map.createPane("depsPane").style.zIndex = 390;
    map.getPane("depsPane").style.pointerEvents = "none";
    map.createPane("communesPane").style.zIndex = 400;
    map.createPane("zonesPane").style.zIndex = 420;
    map.createPane("pinPane").style.zIndex = 650;

    map.pm.setLang("fr");
    map.pm.setGlobalOptions({ allowSelfIntersection: false, snappable: true, snapDistance: 14, pathOptions: { color: "#e8a33d", weight: 3 } });

    const updateLabels = () => {
      elRef.current?.classList.toggle("zone-labels-hidden", map.getZoom() < LABEL_MIN_ZOOM);
      elRef.current?.classList.toggle("pred-labels-hidden", map.getZoom() < LABEL_MIN_ZOOM + 2);
    };
    map.on("zoomend", updateLabels);
    updateLabels();

    map.on("click", (e) => {
      if (map.pm.globalDrawModeEnabled() || editingRef.current) return;
      cbRef.current.onMapClick?.(e.latlng);
    });
    map.on("pm:create", (e) => {
      const geometry = e.layer.toGeoJSON().geometry;
      map.removeLayer(e.layer);
      cbRef.current.onDrawStateChange?.(null);
      cbRef.current.onDrawn?.(geometry);
    });
    map.on("pm:drawend", () => cbRef.current.onDrawStateChange?.(editingRef.current ? "edit" : null));

    mapRef.current = map;
    // Le conteneur peut changer de taille (panneau, rotation mobile)
    const ro = new ResizeObserver(() => map.invalidateSize());
    ro.observe(elRef.current);
    return () => {
      ro.disconnect();
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // --- Fond de carte ---
  useEffect(() => {
    const map = mapRef.current;
    const makeLayer = (key, darken) => {
      const def = TILE_LAYERS[key] || TILE_LAYERS.plan;
      return L.tileLayer(def.url, {
        attribution: def.attribution,
        maxNativeZoom: def.maxNativeZoom,
        maxZoom: def.maxZoom,
        className: darken ? "tiles-dark" : def.className || "",
        // OpenStreetMap refuse les tuiles demandées sans « Referer » (« Access
        // blocked ») : on envoie l'origine du site même si la page (ou le proxy
        // Nginx) impose « Referrer-Policy: same-origin ».
        referrerPolicy: "strict-origin-when-cross-origin",
      });
    };
    const def = TILE_LAYERS[baseLayer] || TILE_LAYERS.plan;
    if (tileRef.current) map.removeLayer(tileRef.current);
    const layer = makeLayer(baseLayer);
    // Secours automatique : si le fournisseur ne répond pas (aucune tuile
    // chargée et plusieurs erreurs), on bascule sur OpenStreetMap.
    if (def.fallback) {
      let loaded = 0, errors = 0;
      layer.on("tileload", () => { loaded++; });
      layer.on("tileerror", () => {
        errors++;
        if (!loaded && errors >= 6 && tileRef.current === layer) {
          map.removeLayer(layer);
          tileRef.current = makeLayer(def.fallback, def.className === "tiles-dark").addTo(map);
        }
      });
    }
    tileRef.current = layer.addTo(map);
  }, [baseLayer]);

  // --- Limites des départements ---
  useEffect(() => {
    const map = mapRef.current;
    if (depLayerRef.current) { map.removeLayer(depLayerRef.current); depLayerRef.current = null; }
    if (!departements || !show.deps) return;
    const dark = baseLayer === "sombre" || baseLayer === "satellite";
    depLayerRef.current = L.geoJSON(departements, {
      pane: "depsPane",
      interactive: false,
      pmIgnore: true,
      style: { color: dark ? "#f0ede4" : "#374151", weight: 1.6, opacity: 0.55, dashArray: "6 5", fill: false },
    }).addTo(map);
  }, [departements, show.deps, baseLayer]);

  // --- Communes ---
  useEffect(() => {
    const map = mapRef.current;
    if (communesLayerRef.current) { map.removeLayer(communesLayerRef.current); communesLayerRef.current = null; }
    if (!communes) return;
    const renderer = L.canvas({ pane: "communesPane", padding: 0.3 });
    communesLayerRef.current = L.geoJSON(communes, {
      pane: "communesPane",
      renderer,
      pmIgnore: true,
      snapIgnore: true,
      onEachFeature: (f, layer) => {
        layer.bindTooltip(() => communeTooltip(f, ctxRef.current), { sticky: true, className: "commune-tip", direction: "top", offset: [0, -6] });
      },
    });
    if (show.communes) communesLayerRef.current.addTo(map);
    restyleCommunes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [communes]);

  const ctxRef = useRef(ctx);
  ctxRef.current = ctx;
  const styleStateRef = useRef({});
  // Communes pré-découpées en quartiers : affichées neutres (ce sont les
  // morceaux, affectés ou non, qui portent les couleurs).
  const predCommunes = new Set((zones || []).filter((z) => z.predecoupage && z.commune_insee).map((z) => z.commune_insee));
  styleStateRef.current = { ctx, hidden, highlight, predCommunes };

  function restyleCommunes() {
    const group = communesLayerRef.current;
    if (!group) return;
    const { ctx: c, hidden: hid, highlight: hl, predCommunes: pred } = styleStateRef.current;
    const hlSet = hl?.communes;
    group.eachLayer((layer) => {
      const p = layer.feature.properties;
      let color, fillOpacity = 0.3, opacity = 0.85, weight = 1, dashArray = null;
      if (p.statut === "cp" || p.statut === "commune") {
        color = tourneeColor(p.tournee, c);
        if (hid.has(p.tournee)) { fillOpacity = 0; opacity = 0.12; }
      } else if (p.statut === "multi") {
        color = MULTI_COLOR; fillOpacity = 0.07; dashArray = "4 4"; opacity = 0.7;
      } else {
        color = NONE_COLOR; fillOpacity = 0.04; opacity = 0.35; dashArray = "2 4";
      }
      if (pred?.has(p.code)) {
        color = NONE_COLOR; fillOpacity = 0.02; opacity = 0.6; dashArray = null; weight = 1.5;
      }
      if (hlSet) {
        if (hlSet.has(p.code)) { weight = 3; fillOpacity = Math.max(fillOpacity, 0.42); opacity = 1; }
        else { fillOpacity = Math.min(fillOpacity, 0.06); opacity = 0.25; }
      }
      layer.setStyle({ color, fillColor: color, fillOpacity, opacity, weight, dashArray });
    });
  }

  useEffect(() => {
    restyleCommunes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx, hidden, highlight, zones]);

  useEffect(() => {
    const map = mapRef.current;
    const g = communesLayerRef.current;
    if (!g) return;
    if (show.communes && !map.hasLayer(g)) g.addTo(map);
    if (!show.communes && map.hasLayer(g)) map.removeLayer(g);
  }, [show.communes]);

  // --- Zones dessinées ---
  useEffect(() => {
    const map = mapRef.current;
    if (editingRef.current) return; // ne pas détruire un tracé en cours de modification
    if (zonesLayerRef.current) { map.removeLayer(zonesLayerRef.current); }
    zoneLayersById.current = {};
    const group = L.featureGroup([], { pane: "zonesPane" });
    if (show.zones) {
      (zones || []).forEach((z) => {
        if (z.tournee && hidden.has(z.tournee)) return;
        const color = zoneColor(z, ctx);
        const dim = highlight?.zones && !highlight.zones.has(z.id);
        // Morceau du pré-découpage pas encore affecté : gris, pointillé, léger.
        const libre = z.predecoupage && !z.tournee;
        const layer = L.GeoJSON.geometryToLayer({ type: "Feature", geometry: z.geometry, properties: {} }, {
          pane: "zonesPane",
          color: libre ? "#9ca3af" : color,
          fillColor: libre ? "#9ca3af" : color,
          fillOpacity: dim ? 0.06 : libre ? 0.12 : 0.33,
          opacity: dim ? 0.35 : libre ? 0.9 : 1,
          weight: highlight?.zones?.has(z.id) ? 4 : libre ? 1.5 : 2.5,
          dashArray: libre ? "5 4" : null,
        });
        layer.options.snapIgnore = false;
        layer._zoneId = z.id;
        if (show.labels) {
          layer.bindTooltip(
            `${escapeHtml(z.nom)}${z.tournee ? `<small>T. ${escapeHtml(z.tournee)}${z.sst ? " · " + escapeHtml(z.sst) : ""}</small>` : ""}`,
            { permanent: true, direction: "center", className: z.predecoupage ? "zone-label zone-label-pred" : "zone-label", interactive: false },
          );
        }
        layer.addTo(group);
        zoneLayersById.current[z.id] = layer;
      });
    }
    group.addTo(map);
    zonesLayerRef.current = group;
  }, [zones, ctx, hidden, highlight, show.zones, show.labels]);

  // --- Repère (adresse recherchée / point cliqué) ---
  useEffect(() => {
    const map = mapRef.current;
    if (markerRef.current) { map.removeLayer(markerRef.current); markerRef.current = null; }
    if (!marker) return;
    const icon = L.divIcon({ className: "pin-icon", html: PIN_SVG, iconSize: [34, 46], iconAnchor: [17, 45] });
    markerRef.current = L.marker([marker.lat, marker.lon], { icon, pane: "pinPane", pmIgnore: true, interactive: false, keyboard: false }).addTo(map);
  }, [marker]);

  useImperativeHandle(ref, () => ({
    startDraw() {
      const map = mapRef.current;
      map.pm.enableDraw("Polygon", { snappable: true, allowSelfIntersection: false, finishOn: null, continueDrawing: false });
      cbRef.current.onDrawStateChange?.("draw");
    },
    cancelDraw() {
      mapRef.current.pm.disableDraw();
      cbRef.current.onDrawStateChange?.(null);
    },
    startEditZone(id) {
      const layer = zoneLayersById.current[id];
      if (!layer) return false;
      layer.closeTooltip?.();
      layer.unbindTooltip();
      layer.pm.enable({ allowSelfIntersection: false, snappable: true, draggable: true });
      editingRef.current = id;
      mapRef.current.fitBounds(layer.getBounds().pad(0.3));
      cbRef.current.onDrawStateChange?.("edit");
      return true;
    },
    finishEditZone() {
      const id = editingRef.current;
      const layer = zoneLayersById.current[id];
      if (!layer) { editingRef.current = null; return null; }
      layer.pm.disable();
      const geometry = layer.toGeoJSON().geometry;
      editingRef.current = null;
      cbRef.current.onDrawStateChange?.(null);
      return { id, geometry };
    },
    cancelEditZone() {
      const id = editingRef.current;
      const layer = zoneLayersById.current[id];
      layer?.pm.disable();
      editingRef.current = null;
      cbRef.current.onDrawStateChange?.(null);
    },
    fitGeo(geojson, maxZoom = 15) {
      try {
        const b = L.geoJSON(geojson).getBounds();
        const wide = mapRef.current.getSize().x >= 768;
        if (b.isValid()) {
          mapRef.current.flyToBounds(b, {
            maxZoom,
            duration: 0.8,
            // laisse la place au panneau latéral (desktop) ou au panneau du haut (mobile)
            paddingTopLeft: wide ? [410, 40] : [20, 160],
            paddingBottomRight: [40, 40],
          });
        }
      } catch {}
    },
    flyTo(lat, lon, zoom = 16) {
      const map = mapRef.current;
      const z = Math.max(zoom, map.getZoom());
      const size = map.getSize();
      // Sur mobile, la fiche résultat couvre le haut de la carte : on place le
      // point plus bas. Sur ordinateur, on le décale à droite du panneau.
      const offset = size.x >= 768 ? L.point(-200, 0) : L.point(0, -size.y * 0.22);
      const target = map.unproject(map.project([lat, lon], z).add(offset), z);
      map.flyTo(target, z, { duration: 0.8 });
    },
    fitOccitanie() {
      mapRef.current.flyToBounds(OCCITANIE_BOUNDS, { duration: 0.8 });
    },
  }));

  return <div ref={elRef} className="absolute inset-0 z-0" data-testid="map" />;
}

function communeTooltip(f, ctx) {
  const p = f.properties;
  const cps = (p.cps || []).join(", ");
  let line;
  if (p.statut === "cp" || p.statut === "commune") {
    const t = ctx.tourneesByCode[p.tournee];
    line = `<b>Tournée ${escapeHtml(p.tournee)}</b>${t?.sst ? " · " + escapeHtml(t.sst) : ""}${p.statut === "commune" && p.source === "manuel" ? " <i>(affectée à la main)</i>" : ""}`;
  } else if (p.statut === "multi") {
    line = `CP partagé : ${p.candidates.map(escapeHtml).join(", ")}`;
  } else {
    line = p.non_affectee ? "<i>Non affectée</i>" : "<i>Aucune tournée</i>";
  }
  return `<div><b>${escapeHtml(p.nom)}</b> <span style="opacity:.7">${escapeHtml(cps)}</span><br/>${line}</div>`;
}
